import React, { useContext, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { HighRiskContext } from "../../../context/HighRiskContext";
import { CircularProgress, Box, Typography, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useTranslation } from "react-i18next";
import useClinicalLocations from "../../../hooks/useClinicalLocations";
import { useObservationsModal } from "../../../../../context/ObservationsModalContext/ObservationsModalContext";
import { getObservationsColumn } from "../../../../../components/Advanced/DataGrid/observationsColumn";

const HighRiskTable = ({ selectedLocation, setSelectedLocation }) => {
  const { t } = useTranslation();
  const { showObservationsModal } = useObservationsModal();
  const { locations: clinicalLocations } = useClinicalLocations();
  const { highRiskPatients, loading, error, setLocationId } = useContext(HighRiskContext);
  const navigate = useNavigate();
  const location = useLocation();
  const tableRef = useRef(null);

  // Scroll to top of table whenever location filter changes
  useEffect(() => {
    if (tableRef.current) {
      setTimeout(() => {
        tableRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, [selectedLocation]);

  const handleLocationChange = (locId) => {
    setSelectedLocation(locId);
    setLocationId(locId === "" ? null : locId);
  };

  const handleViewPatient = (cipId, row) => {
    navigate(`/patient-info/${cipId}`, {
      state: { patientRow: row, returnPath: location.pathname },
    });
  };

  const columns = [
    getObservationsColumn(showObservationsModal, 5, {
      getCipId: (row) => row.varCipId,
      getPatientName: (row) => row.memberFullName || "",
    }),
    {
      field: "actions",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="View Patient Info">
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleViewPatient(params.row.varCipId, params.row);
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    { field: 'memberId', headerName: t("HighRiskInformation.MemberID"), width: 130 },
    { field: 'memberFullName', headerName: t("HighRiskInformation.FullName"), width: 200 },
    { field: 'memberPhone', headerName: t("HighRiskInformation.Phone"), width: 140 },
    { field: 'memberMobile', headerName: t("HighRiskInformation.Mobile"), width: 140 },
    { field: 'ppvEr', headerName: t("HighRiskInformation.PPVER"), width: 100 },
    { field: 'lace', headerName: t("HighRiskInformation.LACE"), width: 80 },
    { field: 'medicalCenter', headerName: t("HighRiskInformation.MedicalCenter"), width: 150 },
    { field: 'status', headerName: t("HighRiskInformation.Status"), width: 100 },
    { field: 'locationAddress', headerName: t("HighRiskInformation.Address"), width: 300 },
    { field: 'primaryCareProvider', headerName: t("HighRiskInformation.PrimaryCareProvider"), width: 200 },
  ];

  const rows = highRiskPatients.map((item) => ({
    id: item.highRiskId,
    ...item,
  }));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Typography color="error">Error fetching data</Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Location Filter - New addition as per plan */}
      <Box sx={{ display: "flex", mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>{t("Main.Admitted.FilterbyInsuranceLocation")}</InputLabel>
          <Select
            label={t("Main.Admitted.FilterbyInsuranceLocation")}
            value={selectedLocation ?? ""}
            onChange={(e) => handleLocationChange(e.target.value)}
          >
            <MenuItem value="">
              <em>{t("Main.Admitted.FilterbyInsuranceLocation")}</em>
            </MenuItem>
            {clinicalLocations.map((loc) => (
              <MenuItem key={loc.id} value={loc.id}>
                {loc.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <Box ref={tableRef} sx={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableRowSelectionOnClick
        />
      </Box>
    </>
  );
};

export default HighRiskTable;
